import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd';
import { forkJoin, Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CONFIG } from 'src/app/constant';
import { ProductsService } from 'src/app/services';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-products-elabel',
  templateUrl: './products-elabel.component.html',
  styleUrls: ['./products-elabel.component.scss'],
})
export class ProductsElabelComponent implements OnInit, OnDestroy {
  productId: string;
  productInfo: any;
  loading = true;
  loadingSubmit = false;
  elabelInfo: any;
  elabelLoading = false;
  attributes: any[] = [];
  generalInfoAttributes: any[] = [];
  attributesLoading = false;
  generalInfoLoading = false;
  groupAttributesLoading = false;
  attributeSets: any[] = [];
  attributeSetsLoading = false;
  selectedAttributeSetId: number | null = null;
  isNewELabel = false;
  private productInfoLoaded = false;
  private elabelDataLoaded = false;
  private attributeSetsLoaded = false;
  private unsubscribe$ = new Subject<void>();
  // Dùng trực tiếp thuộc tính value trên object attr để binding thay vì dùng dict rời
  // selectedValues: { [code: string]: any } = {};

  badgeSettings: number[] = [];

  // Popup T&C E-Label
  isModalVisible = false;
  isTermsAccepted = false;

  // Cấu hình cho ngxSummernote
  config = CONFIG;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productService: ProductsService,
    private notification: NzNotificationService
  ) { }

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe((params) => {
      this.productId = params['id'];
      if (this.productId) {
        this.loadProductInfo();
        this.loadELabelInfo();
        this.loadELabelAttributeSets();
      }
    });
  }

  /** Kiểm tra tất cả API init đã hoàn tất chưa, nếu rồi thì tắt loading */
  private checkAllLoaded(): void {
    if (this.productInfoLoaded && this.elabelDataLoaded && this.attributeSetsLoaded) {
      this.loading = false;
    }
  }

  loadProductInfo(): void {
    this.productService
      .getById(this.productId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res) => {
          if (res.code === 1) {
            this.productInfo = res.data;
            this.prefillElabelDefaults();
          } else {
            this.notification.error('Lỗi', res.message || 'Không thể lấy thông tin sản phẩm');
          }
          this.productInfoLoaded = true;
          this.checkAllLoaded();
        },
        (err) => {
          console.log(err);
          this.notification.error('Lỗi', 'Có lỗi kết nối khi lấy thông tin sản phẩm');
          this.productInfoLoaded = true;
          this.checkAllLoaded();
        }
      );
  }

  loadELabelInfo(): void {
    this.elabelLoading = true;
    this.productService
      .getELabel(this.productId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res) => {
          this.elabelLoading = false;
          if (res.code === 1) {
            this.elabelInfo = res.data;
            this.selectedAttributeSetId = this.elabelInfo.attributeSetId;
            this.fetchAndSyncAttributes();
          } else if (res.code === 4100) {
            this.isNewELabel = true;
            this.fetchAndSyncAttributes();
          } else {
            this.notification.error('Lỗi', res.message || 'Không thể lấy thông tin E-Label');
            this.elabelDataLoaded = true;
            this.checkAllLoaded();
          }
        },
        (err) => {
          console.log(err);
          this.elabelLoading = false;
          this.notification.error('Lỗi', 'Có lỗi kết nối khi lấy thông tin E-Label');
          this.elabelDataLoaded = true;
          this.checkAllLoaded();
        }
      );
  }

  fetchAndSyncAttributes(): void {
    this.generalInfoLoading = true;
    this.groupAttributesLoading = true;

    // Tạo mảng gọi API
    const requests = [
      this.productService.getELabelAttributes({ hasElabel: true, page: 1, pageSize: 200, infoGroup: 'general_information' })
    ];

    if (this.selectedAttributeSetId) {
      requests.push(this.productService.getELabelAttributes({ hasElabel: true, page: 1, pageSize: 200, infoGroup: 'product_group_information', attributeSetId: this.selectedAttributeSetId }));
    }

    forkJoin(requests).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (responses: any[]) => {
        this.generalInfoLoading = false;

        let genRes = responses[0];
        let genItems = (genRes.code === 1 && genRes.data && genRes.data.items) ? genRes.data.items : [];
        genItems = this.mergeWithSaved(genItems, 'general_information');
        this.prepareAttributes(genItems);
        this.assignDisplaySort(genItems);
        this.generalInfoAttributes = genItems;

        if (this.elabelInfo && this.elabelInfo.badgeSettings && this.elabelInfo.badgeSettings.length > 0) {
          this.badgeSettings = [...this.elabelInfo.badgeSettings];
        } else if (!this.elabelInfo || !this.elabelInfo.badgeSettings || this.elabelInfo.badgeSettings.length === 0) {
          // Mặc định 5 trường đầu tiên Auto ON
          this.badgeSettings = this.generalInfoAttributes.slice(0, 5).map(a => a.id);
        }

        if (responses.length > 1) {
          let prodRes = responses[1];
          let prodItems = (prodRes.code === 1 && prodRes.data && prodRes.data.items) ? prodRes.data.items : [];
          prodItems = this.mergeWithSaved(prodItems, 'product_group_information');
          this.prepareAttributes(prodItems);
          this.assignDisplaySort(prodItems);
          this.attributes = prodItems;
        } else {
          this.attributes = [];
        }
        this.groupAttributesLoading = false;

        this.prefillElabelDefaults();
        this.elabelDataLoaded = true;
        this.checkAllLoaded();
      },
      (err) => {
        this.generalInfoLoading = false;
        this.groupAttributesLoading = false;
        console.error('Fetch attributes err:', err);
        this.notification.error('Lỗi', 'Có lỗi kết nối khi đồng bộ thuộc tính');
        this.elabelDataLoaded = true;
        this.checkAllLoaded();
      }
    );
  }

  mergeWithSaved(apiItems: any[], targetGroup: string): any[] {
    const merged = [...apiItems];
    if (this.elabelInfo && this.elabelInfo.attributes && Array.isArray(this.elabelInfo.attributes)) {
      merged.forEach(item => {
        const saved = this.elabelInfo.attributes.find((a: any) => (a.attributeId === item.id) || (a.id === item.id));
        if (saved) {
          item.value = saved.value;
          if (saved.displaySort !== undefined && saved.displaySort !== null) {
            item.displaySort = saved.displaySort;
          }
        }
      });
      const savedInGroup = this.elabelInfo.attributes.filter((a: any) =>
        a.group === targetGroup || (targetGroup === 'product_group_information' && a.group !== 'general_information')
      );
      savedInGroup.forEach((saved: any) => {
        const exists = merged.some(item => item.id === (saved.attributeId || saved.id));
        if (!exists) {
          merged.push({
            ...saved,
            id: saved.attributeId || saved.id,
            valType: saved.valueType || saved.valType
          });
        }
      });
      merged.sort((a, b) => (a.displaySort || 999) - (b.displaySort || 999));
    }
    return merged;
  }

  getSummernoteConfig(placeholderText: string): any {
    return { ...this.config, placeholder: placeholderText || '' };
  }

  /** Gán displaySort cho các item chưa có, giữ nguyên giá trị đã lưu */
  assignDisplaySort(items: any[]): void {
    items.forEach((item, index) => {
      if (item.displaySort === undefined || item.displaySort === null) {
        item.displaySort = index + 1;
      }
    });
  }

  prepareAttributes(items: any[]): void {
    items.forEach((item: any) => {
      if (item.tableConfig && typeof item.tableConfig === 'string') {
        try { item.tableConfig = JSON.parse(item.tableConfig); } catch (e) { }
      }
      if (item.dataSelect && typeof item.dataSelect === 'string') {
        try { item.dataSelect = JSON.parse(item.dataSelect); } catch (e) { }
      }

      // Parse value cho dropdown: API trả về JSON string '{"name":"X"}' hoặc string thuần
      if (item.valType === 'dropdown') {
        if (typeof item.value === 'string' && item.value) {
          try {
            const parsed = JSON.parse(item.value);
            if (parsed && typeof parsed === 'object') {
              item.value = parsed;
            } else {
              item.value = { name: item.value };
            }
          } catch (e) {
            // Nếu không parse được JSON, wrap thành object {name: value}
            item.value = { name: item.value };
          }
        }

        // Validation: Xóa giá trị nếu không còn trong tableConfig (cấu hình các option)
        if (item.value && item.value.name) {
          const exists = Array.isArray(item.tableConfig) && item.tableConfig.some((opt: any) => opt.name === item.value.name);
          if (!exists) {
            item.value = null;
          }
        }
      }

      // Parse value cho select: đảm bảo kiểu dữ liệu khớp với nzValue (opt)
      if (item.valType === 'select') {
        if (typeof item.value === 'string' && item.value) {
          try {
            const parsed = JSON.parse(item.value);
            if (typeof parsed === 'object' && parsed !== null) {
              item.value = parsed;
            }
          } catch (e) { }
        }

        if (item.value !== undefined && item.value !== null) {
          // Xử lý tương thích ngược: item.value có thể là ID cũ hoặc object mới
          const valId = typeof item.value === 'object' && item.value !== null ? item.value.id : item.value;
          const matchingOpt = Array.isArray(item.dataSelect) ? item.dataSelect.find((opt: any) => String(opt.id) === String(valId)) : null;

          if (matchingOpt) {
            item.value = matchingOpt; // Bind chính xác reference object để nz-select hiển thị đúng
          } else {
            item.value = null; // Clear nếu không có trong danh sách
          }
        }
      }

      // Parse value cho multiselect: đảm bảo lưu mảng các object
      if (item.valType === 'multiselect') {
        if (typeof item.value === 'string' && item.value) {
          try {
            const parsed = JSON.parse(item.value);
            if (Array.isArray(parsed)) {
              item.value = parsed;
            }
          } catch (e) { }
        }

        if (!Array.isArray(item.value)) {
          item.value = [];
        }

        // Validation: Lọc bỏ các ID/Object không còn tồn tại trong dataSelect
        if (item.value.length > 0 && Array.isArray(item.dataSelect)) {
          const validValues: any[] = [];
          item.value.forEach((v: any) => {
            const valId = typeof v === 'object' && v !== null ? v.id : v;
            const matchingOpt = item.dataSelect.find((opt: any) => String(opt.id) === String(valId));
            if (matchingOpt) {
              validValues.push(matchingOpt);
            }
          });
          item.value = validValues;
        }
      }

      if (item.valType === 'checkbox') {
        let parsedVal: any = null;
        if (typeof item.value === 'string' && (item.value.trim().startsWith('{') || item.value.trim().startsWith('['))) {
          try { parsedVal = JSON.parse(item.value); } catch (e) { }
        } else if (typeof item.value === 'object' && item.value !== null) {
          parsedVal = item.value;
        }

        if (parsedVal && typeof parsedVal === 'object' && !Array.isArray(parsedVal)) {
          item.value = parsedVal;
        } else {
          item.value = {
            textInput: '',
            checked: item.value === true || item.value === 'true',
            options: []
          };
        }

        // Chuẩn hóa (Normalize) mảng options phẳng để khớp với tableConfig hiện tại
        if (Array.isArray(item.tableConfig) && item.tableConfig.length > 0) {
          const colNames = item.tableConfig.map((c: any) => c.name);
          const currentOptions = Array.isArray(item.value.options) ? item.value.options : [];

          // Phân loại dữ liệu hiện có theo tên cột
          const dataByCol: { [key: string]: any[] } = {};
          colNames.forEach(name => dataByCol[name] = []);

          currentOptions.forEach((opt: any) => {
            if (opt && opt.title && dataByCol[opt.title]) {
              dataByCol[opt.title].push(opt.content);
            }
          });

          // Tính số hàng cần hiển thị (dựa trên cột có nhiều data nhất, tối thiểu 1 hàng nếu đã có data)
          const counts = Object.values(dataByCol).map(arr => arr.length);
          const maxRows = Math.max(...counts, currentOptions.length > 0 ? 1 : 0);

          if (maxRows > 0) {
            const normalizedOptions: any[] = [];
            for (let r = 0; r < maxRows; r++) {
              colNames.forEach(name => {
                normalizedOptions.push({
                  title: name,
                  content: (dataByCol[name] && dataByCol[name][r] !== undefined) ? dataByCol[name][r] : ''
                });
              });
            }
            item.value.options = normalizedOptions;
          } else {
            // Trường hợp chưa có data, tạo 1 hàng trống mặc định
            item.value.options = colNames.map(name => ({ title: name, content: '' }));
          }
        } else {
          // Fallback nếu không có tableConfig
          if (!item.value.options || !Array.isArray(item.value.options)) {
            item.value.options = [{}];
          }
        }
      }

      if (item.valType === 'additional_info') {
        if (typeof item.value === 'string') {
          try { item.value = JSON.parse(item.value); } catch (e) { }
        }
        if (!Array.isArray(item.value) || item.value.length === 0) {
          item.value = [{}];
        }
      }

      if (item.valType === 'default_value' && !item.value) {
        item.value = item.defaultVal;
      }
    });
  }

  prefillElabelDefaults(): void {
    if (this.isNewELabel && this.productInfo) {
      const applyDefault = (attr: any) => {
        if (attr.code === 'TENHANGHOA_ELABEL' && !attr.value) {
          attr.value = this.productInfo.name || this.productInfo.productName || '';
        }
        if (attr.code === 'GTIN_ELABEL' && !attr.value) {
          attr.value = this.productInfo.barcode || '';
        }
      };
      this.generalInfoAttributes.forEach(applyDefault);
      this.attributes.forEach(applyDefault);
    }
  }

  loadELabelAttributes(infoGroup?: string, attributeSetId?: number, isSync: boolean = false): void {
    if (infoGroup === 'general_information') {
      this.generalInfoLoading = true;
      this.generalInfoAttributes = [];
    } else {
      this.groupAttributesLoading = true;
      this.attributes = [];
    }
    const query: any = { hasElabel: true, page: 1, pageSize: 200 };
    if (infoGroup) {
      query.infoGroup = infoGroup;
    }
    if (attributeSetId) {
      query.attributeSetId = attributeSetId;
    }
    this.productService
      .getELabelAttributes(query)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res) => {
          if (infoGroup === 'general_information') {
            this.generalInfoLoading = false;
          } else {
            this.groupAttributesLoading = false;
          }
          if (res.code === 1) {
            let items = (res.data && res.data.items) ? res.data.items : [];
            if (isSync) {
              items = this.mergeWithSaved(items, infoGroup || 'general_information');
            }
            this.prepareAttributes(items);
            this.assignDisplaySort(items);
            if (infoGroup === 'general_information') {
              this.generalInfoAttributes = items;
            } else if (infoGroup === 'product_group_information') {
              this.attributes = items;
            } else {
              this.attributes = items;
            }
            this.prefillElabelDefaults();
          } else {
            this.notification.error('Lỗi', res.message || 'Không thể lấy danh sách thuộc tính');
          }
        },
        (err) => {
          console.log(err);
          if (infoGroup === 'general_information') {
            this.generalInfoLoading = false;
          } else {
            this.groupAttributesLoading = false;
          }
          this.notification.error('Lỗi', 'Có lỗi kết nối khi lấy thuộc tính');
        }
      );
  }

  onAttributeSetChange(setId: number): void {
    this.selectedAttributeSetId = setId;
    // Clear UI và lỗi validate ngay lập tức
    this.attributes = [];
    if (this.validationErrors) {
      Object.keys(this.validationErrors).forEach(key => {
        if (key !== 'selectedAttributeSetId') delete this.validationErrors[key];
      });
    }

    if (setId) {
      delete this.validationErrors['selectedAttributeSetId'];
      this.loadELabelAttributes('product_group_information', setId, false);
    }
  }

  onBadgeToggle(attrId: number, checked: boolean): void {
    if (checked) {
      if (this.badgeSettings.length < 5) {
        this.badgeSettings = [...this.badgeSettings, attrId];
      } else {
        // Vượt quá 5 trường, hiện thông báo
        this.notification.warning('Cảnh báo', 'Chỉ được chọn tối đa 5 trường hiển thị Tóm tắt E-label');
      }
    } else {
      this.badgeSettings = this.badgeSettings.filter(id => id !== attrId);
    }
  }

  loadELabelAttributeSets(): void {
    this.attributeSetsLoading = true;
    const query = { type: 'elabel', page: 1, pageSize: 200 };
    this.productService
      .getELabelAttributeSets(query)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res) => {
          this.attributeSetsLoading = false;
          if (res.code === 1) {
            this.attributeSets = (res.data && res.data.items) ? res.data.items : [];
          } else {
            this.notification.error('Lỗi', res.message || 'Không thể lấy danh sách bộ thuộc tính');
          }
          this.attributeSetsLoaded = true;
          this.checkAllLoaded();
        },
        (err) => {
          console.log(err);
          this.attributeSetsLoading = false;
          this.notification.error('Lỗi', 'Có lỗi kết nối khi lấy bộ thuộc tính');
          this.attributeSetsLoaded = true;
          this.checkAllLoaded();
        }
      );
  }

  parseSelectValue(value: string): any {
    try {
      return JSON.parse(value);
    } catch {
      return { name: value };
    }
  }

  onSelectChange(attr: any, selectedName: string): void {
    if (!selectedName) {
      attr.value = null;
      return;
    }
    const selectedOpt = (attr.tableConfig || []).find((opt: any) => opt.name === selectedName);
    if (selectedOpt) {
      attr.value = { ...selectedOpt };
    } else {
      attr.value = { name: selectedName };
    }
  }

  addCustomRow(attr: any): void {
    if (attr.valType === 'checkbox') {
      if (!attr.value) attr.value = { checked: false, textInput: '', options: [] };
      if (!attr.value.options) attr.value.options = [];
      const colsCount = (attr.tableConfig && attr.tableConfig.length) || 1;
      for (let i = 0; i < colsCount; i++) {
        const colName = (attr.tableConfig && attr.tableConfig[i] && attr.tableConfig[i].name) || '';
        attr.value.options.push({ title: colName, content: '' });
      }
    } else if (attr.valType === 'additional_info') {
      if (!Array.isArray(attr.value)) attr.value = [];
      attr.value.push({});
    }
  }

  removeCustomRow(attr: any, index: number): void {
    if (attr.valType === 'checkbox') {
      const colsCount = (attr.tableConfig && attr.tableConfig.length) || 1;
      const startIdx = index * colsCount;
      if (attr.value && attr.value.options && attr.value.options.length >= startIdx) {
        attr.value.options.splice(startIdx, colsCount);
      }
    } else if (attr.valType === 'additional_info') {
      if (Array.isArray(attr.value) && attr.value.length > 0) {
        attr.value.splice(index, 1);
      }
    }
  }

  getCustomRows(attr: any): any[] {
    if (!attr || !attr.value || !attr.value.options || !Array.isArray(attr.value.options)) return [{}];
    const cCount = (attr.tableConfig && attr.tableConfig.length) || 1;
    const rows = Math.ceil(attr.value.options.length / cCount);
    return Array(rows > 0 ? rows : 1);
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  getCheckboxOptionValue(attr: any, rIdx: number, colName: string): any {
    if (!attr.value || !attr.value.options) return '';
    const colsCount = (attr.tableConfig && attr.tableConfig.length) || 1;
    const startIdx = rIdx * colsCount;
    for (let i = startIdx; i < startIdx + colsCount && i < attr.value.options.length; i++) {
      if (attr.value.options[i].title === colName) {
        return attr.value.options[i].content;
      }
    }
    return '';
  }

  setCheckboxOptionValue(attr: any, rIdx: number, colName: string, val: any): void {
    if (!attr.value) attr.value = { checked: false, textInput: '', options: [] };
    if (!attr.value.options) attr.value.options = [];
    const colsCount = (attr.tableConfig && attr.tableConfig.length) || 1;

    while (attr.value.options.length < (rIdx + 1) * colsCount) {
      attr.value.options.push({ title: '', content: '' });
    }

    const startIdx = rIdx * colsCount;
    for (let i = startIdx; i < startIdx + colsCount; i++) {
      if (attr.value.options[i].title === colName) {
        attr.value.options[i].content = val;
        return;
      }
    }
    for (let i = startIdx; i < startIdx + colsCount; i++) {
      if (!attr.value.options[i].title) {
        attr.value.options[i].title = colName;
        attr.value.options[i].content = val;
        return;
      }
    }
  }

  dropAttribute(event: CdkDragDrop<any[]>, type: string): void {
    const array = type === 'general' ? this.generalInfoAttributes : this.attributes;

    // Perform smooth animated swap provided by Angular CDK
    moveItemInArray(array, event.previousIndex, event.currentIndex);

    // Update displaySort
    array.forEach((attr, i) => {
      attr.displaySort = i + 1;
    });
  }

  trackByAttrCode(index: number, attr: any): string {
    return attr.code;
  }

  onNumberKeypress(event: KeyboardEvent, isInteger: boolean): boolean {
    // Cho phép số
    if (event.key >= '0' && event.key <= '9') {
      return true;
    }
    // Cho phép phím điều hướng, phím xoá
    if (['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'].includes(event.key)) {
      return true;
    }
    // Nếu không phải số nguyên, cho phép nhập duy nhất 1 dấu thập phân (dấu chấm)
    if (!isInteger && event.key === '.') {
      return true;
    }
    // Chặn tất cả các phím khác (bao gồm dấu trừ '-', phím 'e', 'E', dấu phẩy, v.v.)
    event.preventDefault();
    return false;
  }

  onNumberPaste(event: ClipboardEvent, isInteger: boolean): void {
    const pastedData = event.clipboardData ? event.clipboardData.getData('text') : null;
    if (pastedData) {
      // isInteger: Chỉ cho phép digits (^\d+$)
      // float (number): Cho phép digits và dấu thập phân (^\d+(\.\d+)?$)
      const regex = isInteger ? /^\d+$/ : /^\d+(\.\d+)?$/;
      if (!regex.test(pastedData)) {
        event.preventDefault();
      }
    }
  }

  validationErrors: { [key: string]: string } = {};

  /** Strip HTML tags và kiểm tra nội dung thực sự rỗng (xử lý Summernote để lại <p><br></p>) */
  private stripHtml(value: any): string {
    if (!value) return '';
    return String(value).replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  }

  onCheckboxChange(attr: any): void {
    if (attr.value && !attr.value.checked) {
      if (attr.tableConfig && Array.isArray(attr.tableConfig)) {
        attr.value.options = attr.tableConfig.map((c: any) => ({ title: c.name, content: '' }));
      } else {
        attr.value.options = [];
      }
    }
    this.validateFieldOnChange(attr);
  }

  validateFieldOnChange(attr: any, col?: any, rowIdx?: number): void {
    if (col && rowIdx !== undefined) {
      const code = `${attr.code}_${col.name}_${rowIdx}`;
      if (col.isRequired && col.dataType !== 'default_value') {
        const options = attr.value.options || [];
        const matchingOpts = options.filter((o: any) => o.title === col.name);
        const opt = matchingOpts[rowIdx];
        if (!opt || opt.content === undefined || opt.content === null || String(opt.content).trim() === '') {
          const prefix = ['select', 'multiselect', 'dropdown', 'checkbox'].includes(col.dataType) ? 'Vui lòng chọn' : 'Vui lòng nhập';
          this.validationErrors[code] = `${prefix} ${col.name}`;
        } else {
          delete this.validationErrors[code];
        }
      } else {
        delete this.validationErrors[code];
      }
      return;
    }

    if (attr.required) {
      let isEmpty = false;
      if (attr.valType === 'multiselect') {
        isEmpty = !attr.value || (Array.isArray(attr.value) && attr.value.length === 0);
      } else if (attr.valType === 'dropdown') {
        isEmpty = !attr.value || !attr.value.name || String(attr.value.name).trim() === '';
      } else if (attr.valType === 'checkbox') {
        isEmpty = !attr.value || !attr.value.checked;
      } else if (attr.valType === 'additional_info') {
        isEmpty = !Array.isArray(attr.value) || attr.value.length === 0;
      } else if (attr.valType === 'htmleditor') {
        isEmpty = attr.value === undefined || attr.value === null || this.stripHtml(attr.value) === '';
      } else {
        isEmpty = attr.value === undefined || attr.value === null || String(attr.value).trim() === '';
      }

      if (isEmpty) {
        const prefix = ['select', 'multiselect', 'dropdown', 'checkbox'].includes(attr.valType) ? 'Vui lòng chọn' : 'Vui lòng nhập';
        this.validationErrors[attr.code] = `${prefix} ${attr.name}`;
      } else {
        delete this.validationErrors[attr.code];
      }
    } else {
      delete this.validationErrors[attr.code];
    }

    if (attr.valType === 'dropdown') {
      if (attr.value && attr.value.isRequired && attr.value.dataType !== 'default_value') {
        if (attr.value.valueMore === undefined || attr.value.valueMore === null || String(attr.value.valueMore).trim() === '') {
          const prefix = ['select', 'multiselect', 'dropdown', 'checkbox'].includes(attr.value.dataType) ? 'Vui lòng chọn' : 'Vui lòng nhập';
          this.validationErrors[attr.code + '_inner'] = `${prefix} ${attr.value.description || attr.value.name}`;
        } else {
          delete this.validationErrors[attr.code + '_inner'];
        }
      } else {
        delete this.validationErrors[attr.code + '_inner'];
      }
    }

    if (attr.valType === 'checkbox' && attr.addCustomVal && attr.tableConfig) {
      const rows = this.getCustomRows(attr);
      for (let i = 0; i < rows.length; i++) {
        for (const c of attr.tableConfig) {
          const code = `${attr.code}_${c.name}_${i}`;
          if (attr.value && attr.value.checked && c.isRequired && c.dataType !== 'default_value') {
            const options = attr.value.options || [];
            const matchingOpts = options.filter((o: any) => o.title === c.name);
            const opt = matchingOpts[i];
            if (!opt || opt.content === undefined || opt.content === null || String(opt.content).trim() === '') {
              const prefix = ['select', 'multiselect', 'dropdown', 'checkbox'].includes(c.dataType) ? 'Vui lòng chọn' : 'Vui lòng nhập';
              this.validationErrors[code] = `${prefix} ${c.name}`;
            } else {
              delete this.validationErrors[code];
            }
          } else {
            delete this.validationErrors[code];
          }
        }
      }
    }

    // Validate additional_info rows (title + content per row)
    if (attr.valType === 'additional_info' && attr.required && Array.isArray(attr.value)) {
      attr.value.forEach((row: any, rowIdx: number) => {
        const titleCode = `${attr.code}_title_${rowIdx}`;
        const contentCode = `${attr.code}_content_${rowIdx}`;
        if (!row.title || String(row.title).trim() === '') {
          this.validationErrors[titleCode] = 'Vui lòng nhập tiêu đề';
        } else {
          delete this.validationErrors[titleCode];
        }
        if (!row.content || this.stripHtml(row.content) === '') {
          this.validationErrors[contentCode] = 'Vui lòng nhập nội dung';
        } else {
          delete this.validationErrors[contentCode];
        }
      });
    }
  }

  isPdf(url: string): boolean {
    if (!url) return false;
    return /\.(pdf)(\?.*)?$/i.test(url);
  }

  hasWarningImages(): boolean {
    if (!this.elabelInfo || !this.elabelInfo.warning || !this.elabelInfo.warning.documentFiles) return false;
    return this.elabelInfo.warning.documentFiles.some((f: any) => !this.isPdf(f.fileUrl));
  }

  hasWarningLinks(): boolean {
    if (!this.elabelInfo || !this.elabelInfo.warning) return false;
    const hasPdf = this.elabelInfo.warning.documentFiles && this.elabelInfo.warning.documentFiles.some((f: any) => this.isPdf(f.fileUrl));
    const hasLinks = this.elabelInfo.warning.documentLinks && this.elabelInfo.warning.documentLinks.length > 0;
    return !!hasPdf || !!hasLinks;
  }

  save(): void {
    this.validationErrors = {};
    let hasError = false;

    if (!this.selectedAttributeSetId) {
      this.validationErrors['selectedAttributeSetId'] = 'Vui lòng chọn nhóm hàng hóa';
      hasError = true;
    }

    const payloadAttributes: any[] = [];

    // Gom dữ liệu từ cả 2 groups
    const allAttrs = [...this.generalInfoAttributes, ...this.attributes];

    // Validate required fields
    for (const attr of allAttrs) {
      if (attr.required) {
        let isEmpty = false;
        if (attr.valType === 'multiselect') {
          isEmpty = !attr.value || (Array.isArray(attr.value) && attr.value.length === 0);
        } else if (attr.valType === 'dropdown') {
          isEmpty = !attr.value || !attr.value.name || String(attr.value.name).trim() === '';
        } else if (attr.valType === 'checkbox') {
          isEmpty = !attr.value || !attr.value.checked;
        } else if (attr.valType === 'additional_info') {
          isEmpty = !Array.isArray(attr.value) || attr.value.length === 0;
        } else if (attr.valType === 'htmleditor') {
          isEmpty = attr.value === undefined || attr.value === null || this.stripHtml(attr.value) === '';
        } else {
          isEmpty = attr.value === undefined || attr.value === null || String(attr.value).trim() === '';
        }

        if (isEmpty) {
          const prefix = ['select', 'multiselect', 'dropdown', 'checkbox'].includes(attr.valType) ? 'Vui lòng chọn' : 'Vui lòng nhập';
          this.validationErrors[attr.code] = `${prefix} ${attr.name}`;
          hasError = true;
        }
      }

      if (attr.valType === 'dropdown') {
        if (attr.value && attr.value.isRequired && attr.value.dataType !== 'default_value') {
          if (attr.value.valueMore === undefined || attr.value.valueMore === null || String(attr.value.valueMore).trim() === '') {
            const prefix = ['select', 'multiselect', 'dropdown', 'checkbox'].includes(attr.value.dataType) ? 'Vui lòng chọn' : 'Vui lòng nhập';
            this.validationErrors[attr.code + '_inner'] = `${prefix} ${attr.value.description || attr.value.name}`;
            hasError = true;
          }
        }
      }

      // Validate configuration columns inside a verified checkbox
      if (attr.valType === 'checkbox' && attr.value && attr.value.checked && attr.addCustomVal && attr.tableConfig) {
        for (const col of attr.tableConfig) {
          if (col.isRequired && col.dataType !== 'default_value') {
            const options = attr.value.options || [];
            const matchingOpts = options.filter((o: any) => o.title === col.name);
            matchingOpts.forEach((opt: any, rowIdx: number) => {
              if (opt.content === undefined || opt.content === null || String(opt.content).trim() === '') {
                const prefix = ['select', 'multiselect', 'dropdown', 'checkbox'].includes(col.dataType) ? 'Vui lòng chọn' : 'Vui lòng nhập';
                this.validationErrors[`${attr.code}_${col.name}_${rowIdx}`] = `${prefix} ${col.name}`;
                hasError = true;
              }
            });
          }
        }
      }

      // Validate additional_info rows (title + content per row)
      if (attr.valType === 'additional_info' && attr.required && Array.isArray(attr.value)) {
        attr.value.forEach((row: any, rowIdx: number) => {
          const titleCode = `${attr.code}_title_${rowIdx}`;
          const contentCode = `${attr.code}_content_${rowIdx}`;
          if (!row.title || String(row.title).trim() === '') {
            this.validationErrors[titleCode] = 'Vui lòng nhập tiêu đề';
            hasError = true;
          }
          if (!row.content || this.stripHtml(row.content) === '') {
            this.validationErrors[contentCode] = 'Vui lòng nhập nội dung';
            hasError = true;
          }
        });
      }
    }

    if (hasError) {
      // this.notification.warning('Cảnh báo', 'Vui lòng kiểm tra lại các trường bắt buộc');
      return;
    }

    // Nếu form hợp lệ, hiển thị popup xác nhận điều khoản thay vì gọi API ngay
    this.isTermsAccepted = false;
    this.isModalVisible = true;
  }

  confirmSave(): void {
    this.loadingSubmit = true;
    const payloadAttributes: any[] = [];
    const allAttrs = [...this.generalInfoAttributes, ...this.attributes];

    allAttrs.forEach((attr: any) => {
      let finalValue: any = attr.value;
      if (attr.valType === 'checkbox' || attr.valType === 'additional_info') {
        if (attr.value && attr.value.options && Array.isArray(attr.value.options)) {
          attr.value.options.forEach((opt: any) => {
            if (opt.content === null || opt.content === undefined) {
              opt.content = '';
            }
          });
        }
        if (attr.valType === 'checkbox') {
          finalValue = JSON.stringify({ ...attr.value, defaultVal: attr.defaultVal || '' });
        } else {
          finalValue = JSON.stringify(attr.value);
        }
      } else if (Array.isArray(attr.value) && attr.value.length === 0) {
        finalValue = '';
      } else if (typeof attr.value === 'object' && attr.value !== null) {
        finalValue = JSON.stringify(attr.value);
      } else if (attr.value === undefined || attr.value === null) {
        finalValue = '';
      } else {
        finalValue = String(attr.value);
      }

      payloadAttributes.push({
        attributeId: attr.id,
        value: finalValue,
        title: attr.name,
        displaySort: attr.displaySort,
        group: attr.infoGroup,
        valueType: attr.valType
      });
    });

    const tenhanghoaAttr = allAttrs.find(a => a.code === 'TENHANGHOA_ELABEL');
    const body: any = {
      attributeSetId: this.selectedAttributeSetId,
      name: this.productInfo.name || this.productInfo.productName || (tenhanghoaAttr ? tenhanghoaAttr.value : ''),
      badgeSettings: this.badgeSettings,
      attributes: payloadAttributes
    };

    if (this.elabelInfo && this.elabelInfo.version) {
      body.version = this.elabelInfo.version;
    }

    this.productService.upsertELabel(this.productId, body)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res: any) => {
          this.loadingSubmit = false;
          if (res.code === 1 || res.code === 'S001') {
            this.isModalVisible = false;
            this.notification.success('Thành công', 'Lưu thông tin E-Label thành công');
            this.router.navigate(['/business/products']);
          } else {
            this.notification.error('Lỗi', res.message || 'Lỗi khi lưu thông tin E-Label');
          }
        },
        (err: any) => {
          this.loadingSubmit = false;
          console.error(err);
          this.notification.error('Cảnh báo', err);
        }
      );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
